<script>
   /*************************************************************/
   /* ModelCalculator                                           */
   /* -------------------------                                 */
   /* component for training a PCA model                        */
   /*************************************************************/

   import { pca_fit } from '../../../shared/wasm/dist/pca';
   import { app_pca_predict, genhash } from '../../../shared/utils.js';

   import Select from '../../../shared/controls/Select.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';
   import Spinner from '../../../shared/controls/Spinner.svelte';
   import ButtonCancel from '../../../shared/controls/ButtonCancel.svelte';
   import Range from '../../../shared/controls/Range.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let WASM;               // promise with WASM methods
   export let dataset;            // object with training set
   export let outliers;           // vector with dataset row numbers (from 0) marked as outliers

   // outputs
   export let res;                // object with PCA results
   export let model;              // object with PCA model


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // error handling parameetrs
   let status = 'neutral';
   let errMsg = null;

   // model calculation parameters
   let ncomp = 1;
   let center = 'yes';
   let scale = 'no';

   // promise for model calculation method
   let promise;

   /**
    * Fits PCA model and apply it to training set.
    * @param {Object} dataset - object with training set.
    * @param {Number} ncomp - number of PCs in the model.
    * @param {boolean} center - mean center data columns or not.
    * @param {boolean} scale - standardize data columns or not.
    *
    * Method does not return anything but assign values to the global parameters "model" and "res".
    */
   async function computeModel(dataset, ncomp, center, scale) {
      promise = WASM()
         .then(Module => {
            status = 'computing';

            // fit model and add extra parameters
            const m = pca_fit(Module, dataset.values, ncomp, center, scale, outliers);
            m['hash'] = genhash();                  // unique hash number to trace model updates

            m['exclvars'] = [];                            // array with indices of excluded variables - reserved for future use
            m['varlabels'] = dataset.varlabels;            // array with labels for variables
            m['varindices'] = dataset.varindices;          // variable indices
            m['varvalues'] = dataset.varvalues;            // variable values if available (wavelength, etc.)
            m['varvaluesName'] = dataset.varvaluesName;    // title for variable axis
            m['varvaluesUnits'] = dataset.varvaluesUnits;  // units if variales are represented by numeric values, e.g. nm
            m['varrev'] = dataset.varrev                   // if variables must be reversed
            m['exclvars'] = [];                            // array with excluded variables

            m['prep'] = [];                                // array with preprocessing methods — reserved for future use

            // make predictions
            const r = app_pca_predict(Module, m, dataset, outliers);

            // set global variables and status of the component
            model = m;
            res = r;
            status = 'success';
         })
         .catch(e =>{
            // something went wrong
            model = null;
            status = 'error';
            errMsg = e;
         });
   }

   /**
    * Event handler for computing the model
    * @param e — event object.
    */
   function compute(e) {
      status = 'neutral';
      errMsg = null;
      computeModel(dataset, ncomp, center === 'yes', scale === 'yes');
   }

   /**
    * Resets the component status.
    */
   function reset() {
      status = 'neutral';
      promise = null;
      model = null;
      errMsg = null;
   }

   // if data file is empty reset status and error messages
   $: if (dataset === null) reset();

   // set initial number of components depending on dataset size and manual limit of A = 20
   $: maxNComp = dataset ? Math.min(dataset.values.nrows - 2 - outliers.length, dataset.values.ncols - 1) : null;
   $: ncomp = maxNComp ? Math.min(maxNComp, 20) : null;
</script>

{#if (dataset && !model) && status !== 'computing'}
   <!-- settings for global model calculation -->
   <Container label="# of PCs">
      <Range id="ncomp" bind:value={ncomp} min={1} max={maxNComp} step={1} decNum={0} />
   </Container>
   <Container label="Center">
      <Select id="center" bind:value={center} options={["yes", "no"]} />
   </Container>
   <Container label="Scale">
      <Select id="scale" bind:value={scale} options={["yes", "no"]} />
   </Container>
   <Container label=" ">
      <Button on:click={compute}>Create model</Button>
   </Container>
{/if}


{#if promise}

   <!-- calculation is in process -->
   {#await promise}
      <Container>
         <Spinner />
         <p>Computing global model...</p>
      </Container>

   {:then value}
      <!-- status if succeed -->
      {#if model !== null}
      <Container>
         <p>Global model is computed: {model.ncomp} PCs</p>
         <ButtonCancel on:click={reset} />
      </Container>
      {/if}

   {:catch error}
      <!-- error message if failed -->
      <Container status="error">
         <p>{error}</p>
      </Container>
   {/await}

{/if}


<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}
